import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Shield, User, Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StaffManagement = () => {
  const [staff, setStaff] = useState([]);
  const [filteredStaff, setFilteredStaff] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    phone: '',
    password: '',
    role: 'technician'
  });

  useEffect(() => {
    fetchStaff();
  }, []);

  useEffect(() => {
    // Filter staff based on search
    if (searchTerm) {
      const filtered = staff.filter(member =>
        member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.phone.includes(searchTerm) ||
        member.role.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredStaff(filtered);
    } else {
      setFilteredStaff(staff);
    }
  }, [searchTerm, staff]);

  const fetchStaff = async () => {
    try {
      const response = await axios.get(`${API}/admin/staff`);
      setStaff(response.data);
      setFilteredStaff(response.data);
    } catch (error) {
      toast.error('Failed to fetch staff');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingStaff) {
        // Update existing staff
        const updateData = { ...formData };
        if (!updateData.password) {
          delete updateData.password; // Don't update password if not provided
        }
        await axios.put(`${API}/admin/staff/${editingStaff.id}`, updateData);
        toast.success('Staff member updated successfully');
      } else {
        // Create new staff
        await axios.post(`${API}/admin/staff`, formData);
        toast.success('Staff member created successfully');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (member) => {
    setEditingStaff(member);
    setFormData({
      email: member.email,
      name: member.name,
      phone: member.phone,
      password: '', // Don't pre-fill password
      role: member.role
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (staffId) => {
    if (!window.confirm('Are you sure you want to delete this staff member?')) return;
    try {
      await axios.delete(`${API}/admin/staff/${staffId}`);
      toast.success('Staff member deleted');
      fetchStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete staff');
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      name: '',
      phone: '',
      password: '',
      role: 'technician'
    });
    setEditingStaff(null);
  };

  const getRoleBadge = (role) => {
    const colors = {
      admin: 'bg-red-500',
      manager: 'bg-blue-500',
      technician: 'bg-green-500',
      staff: 'bg-purple-500',
      customer: 'bg-slate-500'
    };
    return colors[role] || 'bg-slate-500';
  };

  const getRoleIcon = (role) => {
    if (role === 'admin' || role === 'manager') {
      return <Shield className="h-4 w-4" />;
    }
    return <User className="h-4 w-4" />;
  };

  return (
    <div data-testid="staff-management-page">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-heading font-bold uppercase">Staff & User Management</h2>
          <p className="text-slate-600 text-sm mt-1">Manage technicians, staff, managers, and customers</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary/90" data-testid="add-staff-button">
              <Plus className="h-4 w-4 mr-2" />
              Add Staff Member
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingStaff ? 'Edit Staff Member' : 'Add New Staff Member'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Full Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="John Doe"
                  data-testid="staff-name-input"
                />
              </div>
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  disabled={!!editingStaff}
                  placeholder="staff@example.com"
                  data-testid="staff-email-input"
                />
                {editingStaff && <p className="text-xs text-slate-500 mt-1">Email cannot be changed</p>}
              </div>
              <div>
                <Label>Phone Number *</Label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  placeholder="9876543210"
                  data-testid="staff-phone-input"
                />
              </div>
              <div>
                <Label>Password {editingStaff ? '(Leave blank to keep current)' : '*'}</Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!editingStaff}
                  minLength={6}
                  placeholder={editingStaff ? 'Leave blank to keep current' : 'Minimum 6 characters'}
                  data-testid="staff-password-input"
                />
              </div>
              <div>
                <Label>Role *</Label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-slate-200 rounded-md"
                  data-testid="staff-role-select"
                >
                  <option value="technician">Technician</option>
                  <option value="staff">Staff</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                </select>
                <p className="text-xs text-slate-500 mt-1">
                  Technician: Field service • Staff: Support • Manager: Oversight • Admin: Full access
                </p>
              </div>
              <div className="flex gap-2">
                <Button type="submit" className="flex-1 bg-primary hover:bg-primary/90" data-testid="save-staff-button">
                  {editingStaff ? 'Update Staff Member' : 'Create Staff Member'}
                </Button>
                <Button type="button" variant="outline" onClick={() => { setIsDialogOpen(false); resetForm(); }}>
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-5 w-5" />
          <Input
            type="text"
            placeholder="Search by name, email, phone, or role..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-staff-input"
          />
        </div>
        {searchTerm && (
          <p className="text-sm text-slate-600 mt-2">
            Found {filteredStaff.length} of {staff.length} staff members
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4">
        {filteredStaff.length === 0 ? (
          <Card className="p-8 text-center border-slate-200">
            <p className="text-slate-600">
              {searchTerm ? 'No staff members found matching your search.' : 'No staff members yet. Add your first team member!'}
            </p>
          </Card>
        ) : (
          filteredStaff.map(member => (
            <Card key={member.id} className="p-4 border-slate-200 hover:border-primary/30 transition-colors" data-testid={`staff-item-${member.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4 flex-1">
                  <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                    {getRoleIcon(member.role)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h3 className="font-semibold text-lg">{member.name}</h3>
                      <Badge className={`${getRoleBadge(member.role)} text-white text-xs`}>
                        {member.role}
                      </Badge>
                    </div>
                    <p className="text-sm text-slate-600">{member.email}</p>
                    <p className="text-sm text-slate-600">{member.phone}</p>
                    {member.created_at && (
                      <p className="text-xs text-slate-400 mt-1">
                        Added: {new Date(member.created_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleEdit(member)}
                    data-testid={`edit-staff-${member.id}`}
                  >
                    <Edit className="h-4 w-4 text-blue-600" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    data-testid={`delete-staff-${member.id}`} 
                    onClick={() => handleDelete(member.id)}
                  >
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </Button>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>

      <div className="mt-8 p-4 bg-slate-50 rounded-lg border border-slate-200">
        <h3 className="font-semibold mb-2">Role Descriptions:</h3>
        <ul className="space-y-2 text-sm text-slate-700">
          <li><strong>Technician:</strong> Can view assigned jobs, update service status, access customer contact info</li>
          <li><strong>Staff:</strong> General support staff with limited access</li>
          <li><strong>Manager:</strong> Can assign jobs to technicians, view all orders and services</li>
          <li><strong>Admin:</strong> Full control - manage products, staff, orders, services, and analytics</li>
        </ul>
      </div>
    </div>
  );
};

export default StaffManagement;
